import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material";
import * as clone from "clone";

import { Resource } from "@impulse/sdp-shared-models";

import { ApplicationWizardState } from "./application-wizard/models";
import { ApplicationsService } from "./applications.service";
import { ApplicationWizardService, prettyPrint } from "./application-wizard/application-wizard.service";
import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { ApiGatewayService } from "../../common/api-gateway/api-gateway.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { TranslateService } from "../../common/translate/translate.service";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { ApplicationSelectTypeComponent } from "./application-wizard/application-select-type/application-select-type.component";
import { WizardDialogService } from "../wizard-dialog/wizard-dialog.service";
import { tableDeleteIcon, tableEditIcon } from "../../util";
import { GenericTable } from "../../genericTable";

const MAXIMUM_NUMBER_OF_RESOURCES = 50;

@Component({
	selector: "sdp-applications",
	templateUrl: "./applications.component.html"
})
export class ApplicationsComponent implements OnInit {

	public data: Resource[] | undefined = undefined;
	public isDataLoaded = false;

	public readonly webSchema: GenericTable.ColumnSchema[] = [
		{
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.application"
			},
			bodySchema: {
				type: "string",
				boundField: "name"
			}
		}, {
			minWidth: "40%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.webUrl"
			},
			bodySchema: {
				type: "string",
				boundField: "name",
				displayCallback: (row: Resource) => { return this.getWebUrl(row); }
			}
		}, {
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.portNumbers"
			},
			bodySchema: {
				type: "string",
				boundField: "name",
				displayCallback: (row: Resource) => { return this.getPrettyPrintPorts(row); }
			}
		}, {
			minWidth: "10%",
			type: "button",
			headerSchema: {
				type: "none",
			},
			bodySchema: {
				type: "button",
				buttons: this.getWebButtons()
			}
		}];

	public readonly otherSchema: GenericTable.ColumnSchema[] = [
		{
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.application"
			},
			bodySchema: {
				type: "string",
				boundField: "name"
			}
		}, {
			minWidth: "40%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.ip"
			},
			bodySchema: {
				type: "string",
				boundField: "ip"
			}
		}, {
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.portNumbers"
			},
			bodySchema: {
				type: "string",
				boundField: "name",
				displayCallback: (row: Resource) => { return this.getPrettyPrintPorts(row); }
			}
		}, {
			minWidth: "10%",
			type: "button",
			headerSchema: {
				type: "none",
			},
			bodySchema: {
				type: "button",
				buttons: this.getWebButtons()
			}
		}];

	constructor(
		public dialog: MatDialog,
		private service: ApplicationsService,
		private titleService: BrowserTitleService,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private wizardService: ApplicationWizardService,
		private wizardDialogService: WizardDialogService
	) {
	}

	public async ngOnInit() {
		this.wizardService.submitSuccessEvent.subscribe(async (value: string) => {
			if (value === "success") {
				await this.refresh();
			}
		});
		this.titleService.setTitle("applications.header.title");
		await this.refresh();
	}

	public createDialog() {
		const app: ApplicationWizardState = this.wizardService.newApplicationState();
		this.wizardService.applicationState = app;
		this.wizardService.applicationState.isCreate = true;

		const dialogRef = this.wizardDialogService.openDialog(
			ApplicationSelectTypeComponent,
			{ type: "material", name: "lock_open", cssClass: "sdp-section-header-icon" },
			true
		);
		dialogRef.afterClosed().subscribe(async () => {
			await this.refresh();
		});
	}

	public editDialog(application: Resource) {
		// work with a copy of the application, so temporary changes don't make it to the grid if they cancel
		this.wizardService.applicationState = {
			webUrl: "",
			application: clone(application),
			applicationBeingEdited: application,
			isCreate: false
		};
		if (application.selectType === "website") {
			this.wizardService.applicationState.webUrl = this.wizardService.buildWebUrl(application);
		}

		const dialogRef = this.wizardDialogService.openDialog(
			ApplicationSelectTypeComponent,
			{ type: "material", name: "lock_open", cssClass: "sdp-section-header-icon" },
			false
		);
		dialogRef.afterClosed().subscribe(async () => {
			await this.refresh();
		});
	}

	public async deleteApplication(application: Resource) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
		dialogRef.componentInstance.message = this.translateService.translate(
			"applications.delete.dialog.message",
			{
				applicationName: application.name
			}
		);
		dialogRef.componentInstance.title = "applications.delete.dialog.title";
		dialogRef.componentInstance.buttonAction = "sdp.label.delete";
		dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";
		dialogRef.afterClosed().subscribe(async (result: boolean) => {
			if (result) {
				try {
					await this.apiGatewayService.deleteApplication(application.urlName);
					await this.refresh();
				} catch (error) {
					this.snackbarService.open(this.translateService.translate(error));
				}
			}
		});
	}

	public addDisabled() {
		if (this.isDataLoaded && this.data) {
			if (this.data.length < MAXIMUM_NUMBER_OF_RESOURCES) {
				// room for at least one more app
				return false;
			} else {
				// we've allocated all possible apps
				return true;
			}
		} else {
			// not yet loaded, they can't click on stuff yet
			return true;
		}
	}

	public showApplicationsAtCapacityMessage() {
		return this.isDataLoaded && this.addDisabled();
	}

	public getWebUrl(application: Resource): string {
		return this.wizardService.buildWebUrl(application);
	}

	public getPrettyPrintPorts(application: Resource): string {
		return application.ports.map((port) => {
			return `${prettyPrint(port)}/${port.proto.toUpperCase()}`;
		}).join(", ");
	}

	public get webapps() {
		if (!!this.data) {
			return this.data.filter((app) => {
				return app.selectType === "website";
			});
		}
		return undefined;
	}

	public get otherApps() {
		if (!!this.data) {
			return this.data.filter((app) => {
				return app.selectType === "network";
			});
		}
		return undefined;
	}

	private async refresh() {
		try {
			this.isDataLoaded = false;
			this.data = await this.service.refresh();
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		} finally {
			this.isDataLoaded = true;
		}
	}

	private getWebButtons() {
		const buttons: GenericTable.Button[] = [];
		buttons.push({
			type: "icon",
			icon: tableEditIcon,
			onClick: (row: Resource) => { return this.editDialog(row); }
		});
		buttons.push({
			type: "icon",
			icon: tableDeleteIcon,
			onClick: (row: Resource) => { return this.deleteApplication(row); }
		});
		return buttons;
	}

}
